import { Form, Formik, FormikHelpers } from 'formik';
import React, { memo, useCallback, useState } from 'react';

import { Consent, PartnerMap, PendingConsent } from 'src/reducers/partner/types';

import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
import * as yup from 'Utilities/yup';

import { INVITATION_RESPONSE_CODES } from 'Constants/partnerTypes';

import ErrorField from 'Components/input/ErrorField';

interface ErrorScreen {
  body: string;
  title: string;
}

type InvitationValues = {
  sharingCode: string;
  yob: string;
};

const INVITATION_CODE_FORM_INITIAL_VALUES: InvitationValues = {
  sharingCode: '',
  yob: '',
};

const backToOtherConnections = (
  partners: PartnerMap,
  connected: Consent[],
  pendingConnections: PendingConsent[]
) => {
  mediator.publish('partner:navigateOtherConnections', {
    partners,
    connected,
    pending: pendingConnections,
  });
};

const yobMatcher = new RegExp('^[0-9]{4}$');

type Props = {
  partners: PartnerMap;
  connected: Consent[];
  pendingConnections: PendingConsent[];
};

const InvitationCode: React.FC<React.PropsWithChildren<Props>> = ({
  partners,
  connected,
  pendingConnections,
}) => {
  const [errorScreen, setErrorScreen] = useState<ErrorScreen | null>(null);

  const handleSubmit = useCallback(
    (values: InvitationValues, formikHelpers: FormikHelpers<InvitationValues>) => {
      mediator
        .request<LVApiResponse>('partner:validateCode', {
          code: values.sharingCode,
          yearOfBirth: Number(values.yob),
        })
        .then((result) => {
          switch (result.status) {
            case INVITATION_RESPONSE_CODES.Success:
              mediator.publish('partner:consentScreen', {
                code: values.sharingCode,
                partnerID: result.data.partnerID,
                yearOfBirth: Number(values.yob),
              });

              return;
            case INVITATION_RESPONSE_CODES.IncorrectYOB:
              let msg = i18n.t('Partners.Commercialapi.invalidBirthYearLimit');

              if (result.data.retriesRemaining === 0) {
                msg = i18n.t('Partners.Commercialapi.contactOrgNewCode');

                setErrorScreen({
                  body: msg,
                  title: i18n.t('Partners.Commercialapi.invalidBirthYear'),
                });

                formikHelpers.setSubmitting(false);
                return;
              }

              formikHelpers.setFieldError('yob', msg);

              formikHelpers.setSubmitting(false);

              return;
            case INVITATION_RESPONSE_CODES.InvalidYOB:
              formikHelpers.setFieldError('yob', i18n.t('Partners.Commercialapi.invalidBirthYear'));

              formikHelpers.setSubmitting(false);
              return;
            case INVITATION_RESPONSE_CODES.InvalidCode:
            case INVITATION_RESPONSE_CODES.ExpiredCode:
            case INVITATION_RESPONSE_CODES.NotFound:
              formikHelpers.setFieldError(
                'sharingCode',
                i18n.t('Partners.Commercialapi.contactOrgNewCode')
              );

              formikHelpers.setSubmitting(false);
              return;
            case INVITATION_RESPONSE_CODES.InvalidCodeAndYOB:
              formikHelpers.setFieldError(
                'sharingCode',
                i18n.t('Partners.Commercialapi.contactOrgNewCode')
              );

              formikHelpers.setFieldError('yob', i18n.t('Partners.Commercialapi.invalidBirthYear'));

              formikHelpers.setSubmitting(false);
              return;
            default:
              formikHelpers.setFieldError(
                'sharingCode',
                i18n.t('Partners.Commercialapi.contactOrgNewCode')
              );

              formikHelpers.setSubmitting(false);
          }
        })
        .catch(() => {
          formikHelpers.setSubmitting(false);
        });
    },
    []
  );

  if (errorScreen) {
    return (
      <React.Fragment>
        <span id="sharing-code-expired-text" className="text-xl line-height-md mb-2">
          {errorScreen.title}
        </span>
        <span id="info-sharing-code-expired-text" className="mt-1 mb-2">
          {errorScreen.body}
        </span>
        <button
          id="expiredSharingCode-done-btn"
          className="btn btn-lg btn-primary"
          onClick={() => {
            setErrorScreen(null);
          }}
        >
          {i18n.t('Common.done', { context: 'cap' })}
        </button>
      </React.Fragment>
    );
  }

  return (
    <Formik
      initialValues={INVITATION_CODE_FORM_INITIAL_VALUES}
      validationSchema={yup.object({
        sharingCode: yup
          .string()
          .trim()
          .required(i18n.t('Partners.Commercialapi.invalidSharingCodePattern'))
          .min(7, i18n.t('Partners.Commercialapi.invalidSharingCodePattern'))
          .max(7, i18n.t('Partners.Commercialapi.invalidSharingCodePattern')),
        yob: yup
          .string()
          .trim()
          .matches(yobMatcher, i18n.t('Partners.Commercialapi.invalidBirthYear'))
          .required(i18n.t('Partners.Commercialapi.invalidBirthYear')),
      })}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, isValid, dirty }) => (
        <Form>
          <div className="d-flex flex-column">
            <span id="enterSharingCode-text" className="text-xl line-height-md mb-4">
              {i18n.t('Common.connectWitchCode')}
            </span>

            <span id="sharingCodeInstructions-text" className="text-soft mb-4">
              {i18n.t('Partners.Commercialapi.codeInstructions')}
            </span>

            <div className="d-flex flex-column mb-3 md-form">
              <label
                id="sharingCode-label"
                className="text-soft text-md"
                htmlFor="sharingCode-field"
              >
                {i18n.t('Partners.Commercialapi.code')}
              </label>
              <ErrorField
                id="sharingCode-field"
                type="text"
                className="form-control"
                name="sharingCode"
                maxLength={7}
              />
            </div>

            <span id="sharingCodeDesc-text" className="text-soft mb-4">
              {i18n.t('Partners.Commercialapi.infoSharingCode')}
            </span>

            <div className="d-flex flex-column mb-3 md-form">
              <label id="yob-label" className="text-soft text-md" htmlFor="yob-field">
                {i18n.t('Partners.Commercialapi.wearersBirthYear')}
              </label>
              <ErrorField
                id="yob-field"
                type="text"
                className="form-control"
                name="yob"
                maxLength={4}
              />
            </div>

            <div className="row">
              <div className="col">
                <button
                  id="sharingCode-cancel-btn"
                  className="btn btn-lg btn-block btn-secondary"
                  onClick={() => {
                    backToOtherConnections(partners, connected, pendingConnections);
                  }}
                  type="button"
                >
                  {i18n.t('Common.cancel', { context: 'cap' })}
                </button>
              </div>
              <div className="col">
                <button
                  id="sharingCode-next-btn"
                  className="btn btn-lg btn-block btn-primary"
                  disabled={isSubmitting || !isValid || !dirty}
                  aria-disabled={isSubmitting}
                  type="submit"
                >
                  {i18n.t('Common.next', { context: 'cap' })}
                </button>
              </div>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default memo(InvitationCode);
